import cx from 'classnames';
import {EmployeeRoleLevel, JiraObject, WorkstreamMetric} from 'client';
import {t} from 'i18next';
import {useCallback} from 'react';
import {useWorkstreamMetricMutation} from 'views/workstreams/hooks';
import {useWorkstreamJiraElevations} from 'views/workstreams/jiraObjects';
import {getWorkstreamDri, WorkstreamWithHiddenMnpiMetricsCount} from 'views/workstreams/utils';

import styles from './WorkstreamDetailsShell.module.css';
import {WorkstreamManageOutcomesTrigger} from './WorkstreamManageOutcomesModal';
import {REDESIGN_OUTCOME_FILTERS, RedesignOutcomesList} from './WorkstreamRedesignSections';
import {WorkstreamSectionCard} from './WorkstreamSectionCard';

export const WorkstreamOutcomesSection = ({
  workstream,
}: {
  workstream: WorkstreamWithHiddenMnpiMetricsCount;
}) => {
  const hiddenMnpiMetricsCount = workstream.hidden_mnpi_metrics_count ?? 0;
  const outcomeMetrics = (workstream.metrics ?? []).filter((metric) => metric.type !== 'metric');
  const {
    jiraObjects: resolvedJiraObjects,
    loadElevatableJiraOutcomes,
    elevateJiraOutcome,
    detachJiraOutcome,
  } = useWorkstreamJiraElevations(workstream, true, {refresh: true});
  const outcomes: Array<WorkstreamMetric | JiraObject> = [
    ...outcomeMetrics,
    ...resolvedJiraObjects,
  ];
  const workstreamOwner = getWorkstreamDri(workstream) as EmployeeRoleLevel | undefined;
  const {createMetric, updateMetric, deleteMetric, attachMetric, isPending} =
    useWorkstreamMetricMutation();
  const handleMetricChange = useCallback(
    async (
      metricId: number,
      metricUpdate: Partial<WorkstreamMetric>,
      reason?: string,
      skipAuditLogOrExistingMetric?: boolean | WorkstreamMetric,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Existing issue; this suppression was only added to allow existing lint warnings to be upgraded to errors.
      _disableSnackbar?: boolean
    ) => {
      if (metricId === -1) {
        return createMetric(
          workstream.id,
          metricUpdate,
          (workstream.metrics?.length ?? 0) + 1,
          reason ?? undefined
        );
      }

      const skipAuditLog =
        typeof skipAuditLogOrExistingMetric === 'boolean' ? skipAuditLogOrExistingMetric : false;
      const existingMetric =
        (typeof skipAuditLogOrExistingMetric === 'object' ? skipAuditLogOrExistingMetric : null) ??
        (workstream.metrics ?? []).find((metric) => metric.id === metricId);
      if (!existingMetric) {
        return;
      }

      await updateMetric(
        workstream.id,
        metricId,
        existingMetric,
        metricUpdate,
        reason ?? undefined,
        skipAuditLog
      );
    },
    [createMetric, updateMetric, workstream.id, workstream.metrics]
  );
  const handleDeleteMetric = useCallback(
    async (metric: WorkstreamMetric, reason: string) => {
      await deleteMetric(
        {
          workstreamId: workstream.id,
          metricId: metric.id,
        },
        reason ?? undefined
      );
    },
    [deleteMetric, workstream.id]
  );
  const handleAttachMetric = useCallback(
    async (attachRequest: {metric_id: number; order?: number | null; is_primary?: boolean}) => {
      return attachMetric(workstream.id, attachRequest);
    },
    [attachMetric, workstream.id]
  );
  return (
    <WorkstreamSectionCard
      title={t('milestones')}
      titleAction={
        <WorkstreamManageOutcomesTrigger
          workstreamId={workstream.id}
          workstreamName={workstream.name}
          metrics={workstream.metrics ?? []}
          jiraOutcomes={resolvedJiraObjects}
          initialTab="outcomes"
          autoAddRowOnOpen={!outcomes.length}
          isPending={isPending}
          workstreamOwner={workstreamOwner}
          onSaveMetric={handleMetricChange}
          onAttachMetric={handleAttachMetric}
          onDeleteMetric={handleDeleteMetric}
          loadElevatableJiraOutcomes={loadElevatableJiraOutcomes}
          onElevateJiraOutcome={elevateJiraOutcome}
          onDetachJiraOutcome={detachJiraOutcome}
          metricPeriodOptions={[]}
          triggerVariant="icon"
          triggerLabel={t('edit_milestones').toString()}
          hiddenMnpiMetricsCount={hiddenMnpiMetricsCount}
        />
      }
    >
      <div
        className={cx(styles.updateSectionCard, {
          [styles.updateSectionCardFlush]: outcomes.length === 0,
          [styles.updateSectionCardBorderless]: outcomes.length === 0,
        })}
      >
        <RedesignOutcomesList
          workstreamId={workstream.id}
          workstreamName={workstream.name}
          outcomes={outcomes}
          allEditableMetrics={workstream.metrics ?? []}
          owner={getWorkstreamDri(workstream)}
          selectedFilters={[...REDESIGN_OUTCOME_FILTERS]}
          variant="details"
          handleMetricChange={handleMetricChange}
          onAttachMetric={handleAttachMetric}
          onDelete={handleDeleteMetric}
          loadElevatableJiraOutcomes={loadElevatableJiraOutcomes}
          onElevateJiraOutcome={elevateJiraOutcome}
          onDetachJiraOutcome={detachJiraOutcome}
          isPending={isPending}
        />
      </div>
    </WorkstreamSectionCard>
  );
};
